import { IStringTool } from "../core/IStringTool";
import { RuleParserResult } from "./RuleParserTool";

export interface ValidateInput {
  rules: RuleParserResult[];
  value: any;
  type?: "input" | "beforeinput";
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export class ValidationTool implements IStringTool<ValidateInput, ValidationResult> {
  process({ rules, value, type = "input" }: ValidateInput): ValidationResult {
    const errors: string[] = [];
    for (const rule of rules) {
      var beforeInputValidatorFn: any = null;
      var inputValidatorFn: any = null;
      var inputValid = true;
      var beforeInputValid = true;

      if (type == "input") {
        inputValidatorFn = this.inputValidators[rule.key];
        if (!inputValidatorFn) continue;
      }
      if (type == "beforeinput") {
        beforeInputValidatorFn = this.beforeInputValidators[rule.key];
        if (!beforeInputValidatorFn) continue;
      }

      if (inputValidatorFn) {
        inputValid = inputValidatorFn(value, ...(rule.params || []));
      }
      if (beforeInputValidatorFn) {
        beforeInputValid = beforeInputValidatorFn(value, ...(rule.params || []));
      }

      if (!beforeInputValid) {
        errors.push(this.getErrorMessage(rule.key, rule.params));
      }
      if (!inputValid) {
        errors.push(this.getErrorMessage(rule.key, rule.params));
      }
    }
    return { valid: errors.length === 0, errors };
  }

  inputValidators: Record<string, (value: any, ...params: string[]) => boolean> = {
    required: (v) => v != null && String(v).trim() !== "",
    min: (v, min) => typeof v === "string" && v.length >= Number(min),
    checked: (v) => v === true || v === "true" || v === 1 || v === "1",
    email: (v) => typeof v === "string" && /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(v),
    max: (v, max) => typeof v === "string" && v.length <= Number(max),
    phone: (v) =>
      typeof v === "string" &&
      /^(\+?\d{1,3}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?[\d\-.\s]{5,15}$/.test(v),
    number: (v) => typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v),
    url: (v) =>
      typeof v === "string" &&
      /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/.test(v),
  };

  beforeInputValidators: Record<string, (value: any, ...params: string[]) => boolean> = {
    number: (v) => /^-?\d*\.?\d*$/.test(v),
    integer: (v) => /^-?\d*$/.test(v),
    email: (v) => /^[\w@.\-+]*$/.test(v),
    phone: (v) =>
      /^(\+)?[\d\-() ]*$/.test(v) &&
      (v.match(/\+/g)?.length ?? 0) <= 1 &&
      (!v.includes("+") || v.indexOf("+") === 0),
    letters: (v) => /^[a-zA-Z]*$/.test(v),
    lettersSpaces: (v) => /^[a-zA-Z\s]*$/.test(v),
    lettersNumbers: (v) => /^[a-zA-Z0-9]*$/.test(v),
    url: (v) => /^[a-zA-Z0-9\-._~:/?#\[\]@!$&'()*+,;=%]*$/.test(v),
    pattern: (v, p) => {
      try {
        return new RegExp(p || "").test(v);
      } catch {
        return true;
      }
    },
  };

  getErrorMessage(key: string, params?: string[]): string {
    switch (key) {
      case "required":
        return "This field is required";
      case "email":
        return "Invalid email address";
      case "min":
        return `Minimum ${params?.[0]} characters`;
      case "max":
        return `Maximum ${params?.[0]} characters`;
      case "phone":
        return "Invalid phone number";
      case "number":
        return "Only numbers are allowed";
      case "url":
        return "Invalid URL address";
      case "checked":
        return "You must accept";
      default:
        return "Invalid value";
    }
  }
}
